'use client';
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cx } from '../../../internal/cx.js';
import { areaPath, barPath, chartColor, createValueFormatter, isolatedPoints, linePath, linearScale, seriesStats, useElementWidth, } from '../../../internal/charts/index.js';
const PAD = 2;
function toneFor(last, t) {
    if (!t || last === undefined)
        return undefined;
    const past = (limit) => limit !== undefined && (t.direction === 'below' ? last <= limit : last >= limit);
    if (past(t.danger))
        return 'danger';
    if (past(t.warning))
        return 'warning';
    return 'neutral';
}
/**
 * A word-sized trend line, area or sparkbar for tables, stat cards and
 * lists. Gaps break the line, `invert` suits ranks, thresholds set the tone,
 * and a `label` turns it into an image with a min / max / latest summary.
 */
export const Sparkline = forwardRef(function Sparkline({ values, variant = 'line', width: widthProp = 80, height = 24, responsive = false, invert = false, domain = ['auto', 'auto'], tone = 'current', color, thresholds, markers = 'last', label, summary, locale, valueFormat, formatValue, labels, className, style, ...rest }, ref) {
    const [measureRef, measured] = useElementWidth();
    const width = responsive ? (measured ?? widthProp) : widthProp;
    const clean = values.map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : null));
    const stats = seriesStats(clean);
    const enough = variant === 'bar' ? (stats?.count ?? 0) >= 1 : (stats?.count ?? 0) >= 2;
    const thresholdTone = toneFor(stats?.last, thresholds);
    const resolvedTone = thresholdTone ?? tone;
    const fmt = createValueFormatter(locale, valueFormat, formatValue);
    const setRefs = (node) => {
        if (responsive)
            measureRef(node);
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    };
    const rootProps = {
        ...rest,
        ref: setRefs,
        className: cx('mn-sparkline', className),
        'data-variant': variant,
        'data-tone': color === undefined ? resolvedTone : undefined,
        'data-responsive': responsive || undefined,
        style: {
            ...(color !== undefined ? { ['--_c']: chartColor(color) } : {}),
            ...(responsive ? {} : { width: widthProp }),
            height,
            ...style,
        },
    };
    if (!enough || !stats) {
        const text = labels?.notEnoughData ?? 'Not enough data';
        return (_jsx("span", { ...rootProps, "data-empty": "", children: _jsxs("span", { className: "mn-sparkline__empty", children: [label ? _jsxs("span", { className: "mn-visually-hidden", children: [label, ": "] }) : null, text] }) }));
    }
    let lo = domain[0] === 'auto' ? stats.min : domain[0];
    let hi = domain[1] === 'auto' ? stats.max : domain[1];
    if (variant === 'bar') {
        lo = Math.min(lo, 0);
        hi = Math.max(hi, 0);
    }
    if (lo === hi) {
        lo -= 1;
        hi += 1;
    }
    const top = PAD;
    const bottom = height - PAD;
    const y = linearScale([lo, hi], invert ? [top, bottom] : [bottom, top]);
    const count = clean.length;
    let marks;
    if (variant === 'bar') {
        const band = width / count;
        const gap = band > 4 ? 1 : 0;
        const zero = y(Math.max(lo, Math.min(0, hi)));
        marks = clean.map((v, i) => {
            if (v === null)
                return null;
            const p = y(v);
            const up = invert ? p > zero : p < zero;
            const yTop = Math.min(p, zero);
            const h = Math.max(1, Math.abs(zero - p));
            return (_jsx("path", { className: "mn-sparkline__bar", d: barPath(i * band + gap / 2, yTop, Math.max(1, band - gap), h, 1, up ? 'top' : 'bottom') }, i));
        });
    }
    else {
        const step = count > 1 ? (width - PAD * 2) / (count - 1) : 0;
        const pts = clean.map((v, i) => v === null ? null : { x: PAD + i * step, y: y(v) });
        const lastIndex = clean.reduce((acc, v, i) => (v === null ? acc : i), -1);
        const dots = markers === 'all'
            ? pts.filter((p) => p !== null)
            : [
                ...isolatedPoints(pts),
                ...(markers === 'last' && lastIndex >= 0
                    ? [pts[lastIndex]].filter((p) => Boolean(p))
                    : []),
            ];
        const baseline = invert ? top : bottom;
        marks = (_jsxs(_Fragment, { children: [variant === 'area' && _jsx("path", { className: "mn-sparkline__area", d: areaPath(pts, baseline) }), _jsx("path", { className: "mn-sparkline__line", d: linePath(pts) }), (!responsive || measured !== undefined) &&
                    dots.map((p, i) => (_jsx("circle", { className: "mn-sparkline__dot", cx: p.x, cy: p.y, r: 2 }, i)))] }));
    }
    const status = thresholdTone === 'danger'
        ? (labels?.danger ?? 'Critical')
        : thresholdTone === 'warning'
            ? (labels?.warning ?? 'Warning')
            : undefined;
    const text = label
        ? (summary ??
            [
                (labels?.summary ??
                    ((l, last, min, max) => `${l}: latest ${last}, low ${min}, high ${max}`))(label, fmt(stats.last), fmt(stats.min), fmt(stats.max)),
                status,
            ]
                .filter(Boolean)
                .join('. '))
        : undefined;
    return (_jsx("span", { ...rootProps, ...(text ? { role: 'img', 'aria-label': text } : { 'aria-hidden': true }), children: _jsx("svg", { className: "mn-sparkline__svg", width: responsive ? '100%' : width, height: height, viewBox: `0 0 ${width} ${height}`, preserveAspectRatio: responsive && measured === undefined ? 'none' : undefined, "aria-hidden": "true", focusable: "false", children: marks }) }));
});
//# sourceMappingURL=sparkline.js.map